import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "../../components/BottomNav";
import AccountHeader from "./AccountHeader";
import ActionGrid from "./ActionGrid";
import BalanceCard from "./BalanceCard";
import ReferralBar from "./ReferralBar";
import { useAuth } from "../../context/AuthContext";
import { walletApi } from "../../api/wallet";
import referralApi from "../../api/referral";
import { useWebSocket } from '../../context/WebSocketContext';
import { useBottomNav } from '../../context/BottomNavContext';

export default function Account() {
  const { user, tokens } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const { paymentStatus } = useWebSocket();
  const { height, isIOS, updateHeight } = useBottomNav();
  const [bottomPadding, setBottomPadding] = useState(100);
  const [referralCode, setReferralCode] = useState(null);
  
  useEffect(() => {
    updateHeight && updateHeight();
    
    // Get the safe area bottom value for iOS
    let safeAreaBottom = 0;
    if (isIOS) {
      const safeAreaValue = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom').trim();
      if (safeAreaValue && safeAreaValue !== '0px') {
        safeAreaBottom = parseInt(safeAreaValue, 10) || 0;
      }
    }
    
    // Calculate padding with consideration for the toolbar and URL bar
    const extraIOSSpace = isIOS ? 20 : 0; // Reduced from 50 to 20
    const padding = isIOS ? height + 16 + safeAreaBottom + extraIOSSpace : height + 8; // Reduced values
    setBottomPadding(padding);
    
    const timer = setTimeout(() => {
      updateHeight && updateHeight();
      // Update padding with the same consideration
      const latestPadding = isIOS ? height + 16 + safeAreaBottom + extraIOSSpace : height + 8; // Reduced values
      setBottomPadding(latestPadding);
    }, 200);
    
    return () => clearTimeout(timer);
  }, [height, isIOS, updateHeight]);
  
  const fetchWallet = async () => {
    try {
      console.log('Fetching wallet for user:', user?.id);
      const wallet = await walletApi.getWallet(tokens.access.token, user.id);
      
      // Check if we got a redirected response (token expired)
      if (wallet && wallet.redirected) {
        console.log('Token expired during wallet fetch in account page');
        return;
      }
      
      console.log('Wallet fetched:', wallet);
      setWallet(wallet);
    } catch (error) {
      console.error('Error fetching wallet:', error);
      // Don't rethrow to prevent UI issues
    }
  }

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        const referral = await referralApi.getReferral(tokens.access.token);
        if (referral.status === 200) {
          setReferralCode(referral.data.referralCode.code);
        } else {
          // Generate new referral code if get request fails
          const newReferral = await referralApi.generateReferral(tokens.access.token);
          console.log("Generated new referral:", newReferral.data);
          setReferralCode(newReferral.data.referralCode?.code || newReferral.data.code);
        }
      } catch (error) {
        console.log("Error in referral flow:", error);
        // Handle 404 or any other error by generating a new code
        try {
          const newReferral = await referralApi.generateReferral(tokens.access.token);
          console.log("Generated referral after error:", newReferral.data);
          setReferralCode(newReferral.data.referralCode?.code || newReferral.data.code);
        } catch (genError) {
          console.error("Error generating referral:", genError);
        }
      }
    };
    
    if (tokens.access.token) {
      fetchReferral();
    }
  }, [tokens.access.token]);

  const moneyFormat = (amount) => {
    if (!amount) return 'PHP 0.00';
    return `${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  const getRole = () => {
    switch (user?.roleId) {
      case 1:
        return 'Admin'
      case 2:
        return 'Incorporator'
      case 3:
        return 'Sub Operator'
      case 4:
        return 'General Coordinator'
      case 5:
        return 'Coordinator'
      case 6:
        return 'Sub Coordinator'
      case 7:
        return 'Agent'
      case 8:
        return 'Sub Agent'
      case 9:
        return 'Player'
      default:
        return 'Player'
    }
  }

  useEffect(() =>  {
    setUserInfo(user);
    if (user && tokens?.access?.token) {
      console.log('User authenticated, fetching wallet...');
      fetchWallet();
    }
  }, [user, tokens]);

  console.log('Account component rendering:', { 
    hasUser: !!user, 
    hasToken: !!tokens?.access?.token,
    userInfo,
    wallet
  });
  
  return (
    <div className="min-h-screen relative font-poppins">
      <div 
        className="absolute top-0 left-0 right-0 bottom-0 overflow-y-auto hide-scrollbar"
        style={{ 
          paddingBottom: `${bottomPadding}px`, // Removed additional safety margin
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          maxWidth: '500px',
          margin: '0 auto'
        }}
      >

        <AccountHeader
          name={user?.name}
          role={getRole()}
          email={user?.email}
          phone={user?.mobileNumber}
        />
        
        {user.roleId !== 9 && (
          <ReferralBar referralCode={referralCode} />
        )}
        <BalanceCard balance={moneyFormat(wallet?.balance)} />
        <div className="mt-2 pb-16"> {/* Reduced from pb-32 and removed bottom-space class */}
          <ActionGrid userInfo={user} wallet={wallet} />
        </div>
      </div>
      <BottomNav />
    </div>
  )
}

